<!-- Tab bar -->
<div class="flex border-b border-slate-200 px-6" role="tablist">
  <button
    *ngFor="let tab of tabs"
    (click)="selectedTab = tab.id"
    role="tab"
    [attr.aria-selected]="selectedTab === tab.id"
    class="mr-6 border-b-2 py-3 text-sm font-medium transition-colors focus:outline-none"
    [class.border-violet-600]="selectedTab === tab.id"
    [class.text-violet-700]="selectedTab === tab.id"
    [class.border-transparent]="selectedTab !== tab.id"
    [class.text-slate-500]="selectedTab !== tab.id"
    [class.hover:text-slate-700]="selectedTab !== tab.id"
  >
    {{ tab.label }}
  </button>
</div>

<!-- ── Overview ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'overview'" class="p-6" role="tabpanel">

  <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Services</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ totalCount }}</p>
    </div>
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Monitored</p>
      <p class="mt-1 text-2xl font-bold text-emerald-700">{{ monitoredCount }}</p>
    </div>
    <div class="rounded-lg border p-4"
         [class.border-amber-200]="warningCount > 0"
         [class.bg-amber-50]="warningCount > 0"
         [class.border-slate-200]="warningCount === 0"
         [class.bg-slate-50]="warningCount === 0">
      <p class="text-xs font-medium uppercase tracking-wide"
         [class.text-amber-500]="warningCount > 0"
         [class.text-slate-400]="warningCount === 0">Degraded / Task Count Low</p>
      <p class="mt-1 text-2xl font-bold"
         [class.text-amber-700]="warningCount > 0"
         [class.text-slate-900]="warningCount === 0">{{ warningCount }}</p>
    </div>
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Active Services</p>
      <p class="mt-1 text-2xl font-bold text-emerald-700">{{ activeCount }}</p>
    </div>
  </div>

</div>

<!-- ── Services ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'services'" role="tabpanel">

  <!-- Filter bar spans full width -->
  <app-cloud-filter-bar
    entityName="services"
    placeholder="Search name, cluster, or ARN…"
    [facets]="facets"
    [sortOptions]="sortOptions"
    [filters]="filters"
    [resultCount]="filteredServices.length"
    [totalCount]="ecsServices.length"
    [quotaUsage]="quotaUsage"
    (filtersChange)="onFiltersChange($event)"
  ></app-cloud-filter-bar>

  <div class="flex min-h-0" [class.divide-x]="selectedSvc" [class.divide-slate-200]="selectedSvc">

    <!-- Table panel -->
    <div class="min-w-0 flex-1 p-6">

      <div *ngIf="loading" class="flex items-center gap-2 py-6 text-sm text-slate-400">
        <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        Loading ECS services…
      </div>

      <div *ngIf="!loading" class="overflow-x-auto">
        <table class="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr class="border-b border-slate-200">
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Service</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Cluster</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Tasks</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Launch Type</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Monitoring</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Routing</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let svc of filteredServices"
                class="border-b border-slate-100 last:border-0"
                [class.bg-violet-50]="svc.arn === selectedSvc">
              <td class="py-3 pr-4">
                <span class="font-mono text-xs font-medium text-slate-900">{{ svc.name }}</span>
                <p class="mt-0.5 truncate font-mono text-[10px] text-slate-400 max-w-xs">{{ svc.arn }}</p>
              </td>
              <td class="py-3 pr-4">
                <span class="font-mono text-xs text-slate-700">{{ svc.clusterName }}</span>
              </td>
              <td class="py-3 pr-4">
                <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
                      [class]="statusClass(svc.status)">
                  {{ svc.status }}
                </span>
              </td>
              <td class="py-3 pr-4">
                <span class="text-xs font-medium"
                      [class.text-amber-700]="svc.desiredCount > 0 && svc.runningCount < svc.desiredCount"
                      [class.text-slate-700]="!(svc.desiredCount > 0 && svc.runningCount < svc.desiredCount)">
                  {{ svc.runningCount }}/{{ svc.desiredCount }}
                </span>
              </td>
              <td class="py-3 pr-4">
                <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-100 text-slate-600">
                  {{ svc.launchType }}
                </span>
              </td>
              <!-- Monitoring -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <ng-container *ngIf="isMonitored(svc.arn); else ecsNotMonitored">
                  <button
                    type="button"
                    (click)="toggleMonitoring(svc.arn)"
                    class="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    aria-label="Disable monitoring"
                  >
                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                    Monitored
                  </button>
                </ng-container>
                <ng-template #ecsNotMonitored>
                  <ng-container *ngIf="canMonitorMore; else ecsQuotaFull">
                    <button
                      type="button"
                      (click)="toggleMonitoring(svc.arn)"
                      class="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    >
                      Monitor
                    </button>
                  </ng-container>
                  <ng-template #ecsQuotaFull>
                    <span class="group relative inline-block">
                      <button
                        type="button"
                        (click)="toggleMonitoring(svc.arn)"
                        aria-disabled="true"
                        class="cursor-not-allowed rounded border border-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-300 focus:outline-none"
                      >
                        Monitor
                      </button>
                      <span class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] leading-tight text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        Quota reached
                      </span>
                    </span>
                  </ng-template>
                </ng-template>
              </td>
              <!-- Routing -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <div class="flex items-center gap-1.5">
                  <span *ngIf="itemOverrideSummary(svc.arn)"
                        class="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
                    {{ itemOverrideSummary(svc.arn) }}
                  </span>
                  <button
                    type="button"
                    (click)="openItemSettings(svc.arn)"
                    class="flex h-5 w-5 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    [class.text-violet-500]="!!itemOverrideSummary(svc.arn)"
                    [attr.aria-label]="'Configure routing for ' + svc.name"
                  >
                    <lucide-icon name="settings" [size]="12" [strokeWidth]="2"></lucide-icon>
                  </button>
                </div>
              </td>
              <!-- View events -->
              <td class="py-3 pr-2 whitespace-nowrap">
                <button
                  type="button"
                  (click)="svc.arn === selectedSvc ? closeSvc() : selectSvc(svc.arn)"
                  class="rounded border px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.border-violet-300]="svc.arn === selectedSvc"
                  [class.bg-violet-50]="svc.arn === selectedSvc"
                  [class.text-violet-700]="svc.arn === selectedSvc"
                  [class.border-slate-200]="svc.arn !== selectedSvc"
                  [class.text-slate-500]="svc.arn !== selectedSvc"
                  [class.hover:border-slate-300]="svc.arn !== selectedSvc"
                  [class.hover:text-slate-700]="svc.arn !== selectedSvc"
                >
                  {{ svc.arn === selectedSvc ? 'Close' : 'View events' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty state: no matches -->
        <div *ngIf="filteredServices.length === 0 && ecsServices.length > 0" class="py-10 text-center">
          <lucide-icon name="search" [size]="28" class="mx-auto mb-2 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
          <p class="text-sm font-medium text-slate-500">No services match your filters</p>
          <p class="mt-0.5 text-xs text-slate-400">Try adjusting the search or clearing active filters.</p>
        </div>

        <!-- Empty state: no data -->
        <p *ngIf="ecsServices.length === 0" class="py-6 text-sm text-slate-400">
          No ECS services found in this account and region.
        </p>
      </div>

    </div>

    <!-- Service events panel -->
    <div *ngIf="selectedSvc" class="flex w-96 shrink-0 flex-col">
      <!-- Header -->
      <div class="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <lucide-icon name="clock" [size]="15" class="shrink-0 text-slate-400" [strokeWidth]="2"></lucide-icon>
        <div class="min-w-0 flex-1">
          <p class="text-xs font-semibold text-slate-700">Service Events</p>
          <p class="truncate font-mono text-[10px] text-slate-400">{{ selectedSvc }}</p>
        </div>
        <button
          type="button"
          (click)="closeSvc()"
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
          aria-label="Close"
        >
          <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
        </button>
      </div>

      <!-- Loading -->
      <div *ngIf="svcEventsLoading" class="flex items-center gap-2 px-4 py-6 text-sm text-slate-400">
        <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        Loading events…
      </div>

      <!-- Error -->
      <p *ngIf="svcEventsError" class="px-4 py-4 text-xs text-red-500">{{ svcEventsError }}</p>

      <!-- List -->
      <div *ngIf="!svcEventsLoading && !svcEventsError" class="flex-1 overflow-y-auto">
        <div *ngIf="svcEvents.length === 0" class="px-4 py-6 text-xs text-slate-400">No events found.</div>
        <div *ngFor="let ev of pagedSvcEvents"
             class="border-b border-slate-100 px-4 py-3 last:border-0">
          <p class="text-[11px] text-slate-700">{{ ev.message }}</p>
          <p *ngIf="ev.createdAt" class="mt-0.5 text-[10px] text-slate-400">{{ ev.createdAt | cloudDate }}</p>
        </div>
      </div>

      <!-- Pagination -->
      <div *ngIf="svcPageTotal > 1"
           class="flex items-center justify-between border-t border-slate-200 px-4 py-2">
        <button
          type="button"
          (click)="svcPage = svcPage - 1"
          [disabled]="svcPage === 1"
          class="rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100 disabled:opacity-40 focus:outline-none"
        >← Prev</button>
        <span class="text-[11px] text-slate-400">{{ svcPage }} / {{ svcPageTotal }}</span>
        <button
          type="button"
          (click)="svcPage = svcPage + 1"
          [disabled]="svcPage === svcPageTotal"
          class="rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100 disabled:opacity-40 focus:outline-none"
        >Next →</button>
      </div>
    </div>

  </div>

</div>

<!-- ── Activity ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'activity'" role="tabpanel">
  <app-cloud-activity-feed
    [items]="activity"
    [loading]="activityLoading"
    serviceLabel="ECS"
    emptyHint="Deployment-failure, low-task-count, and unhealthy-service findings appear here."
    (itemSelect)="onActivitySelect($event)"
  ></app-cloud-activity-feed>
</div>

<!-- ── Item settings drawer ──────────────────────────────────────── -->
<app-item-settings-drawer
  [service]="serviceCard"
  [itemId]="drawerItemId"
  [open]="drawerItemId !== null"
  (closed)="closeItemSettings()"
></app-item-settings-drawer>
